import React, { useEffect, useState } from 'react';
import withTheme from '@material-ui/styles/withTheme';
import {
    Grid,
    CircularProgress,
    MenuItem,
    Select,
    InputLabel,
    Checkbox,
    FormControlLabel
} from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { ErrorIcon } from '../../../../icons';
import { useHistory } from 'react-router';
import { useNotify } from '../../../../hooks/useNotify';
import { doSwaggerCall } from '../../../../hooks/useSwaggerClient';
import FormSection from '../../../../components/atom/FormSection';
import AdminHeader from '../../../../components/atom/AdminHeader';
import { PAYMENT_METHOD_LIST } from '../../../../hooks/usePaymentMethod';
import GlcodeEditor from '../../../../components/glcode/GlcodeEditor';

const EMPTY_PAYMENT_METHOD = {
    id: 'new',
    slug: '',
    name: '',
    methodtype: 'cash',
    note_required: false,
    gl_code: {}
};

function PaymentMethodEditForm({ theme, clientId, methodId }) {
    const { notifyError, notifySuccess } = useNotify();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [paymentMethod, setPaymentMethod] = useState({ ...EMPTY_PAYMENT_METHOD });
    useEffect(() => {
        if (methodId === 'new') {
            setLoading(false);
            return;
        }
        doSwaggerCall('clients', 'getPaymentMethod', {
            clientId: parseInt(clientId, 10),
            methodId: parseInt(methodId, 10)
        })
            .then(res => {
                setPaymentMethod(res);
                setLoading(false);
            })
            .catch(err => {
                console.log(err);
                setError('Could not get payment method!');
            });
    }, [clientId, methodId]);

    const modifyPaymentMethod = async () => {
        try {
            if (methodId === 'new') {
                await doSwaggerCall('clients', 'createPaymentMethod', {
                    clientId: parseInt(clientId, 10),
                    newPaymentMethod: paymentMethod
                });
            } else {
                await doSwaggerCall('clients', 'editPaymentMethod', {
                    clientId: parseInt(clientId, 10),
                    methodId: parseInt(methodId, 10),
                    newPaymentMethod: paymentMethod
                });
            }
            notifySuccess('PaymentMethod modified!');
            history.push(`/admin/client/${clientId}/paymentmethods`);
        } catch (err) {
            console.log(err);
            notifyError('Could not modify payment method!');
        }
    };

    const paymentMethodTypeDropdown = PAYMENT_METHOD_LIST.map(e => {
        return {
            value: e.id,
            name: `${e.id}: ${e.name}`
        };
    });

    if (error && methodId !== 'new') {
        return (
            <Grid container justifyContent="center">
                <h1>{`${error}`}</h1>
            </Grid>
        );
    }
    if (loading) {
        return (
            <Grid container justifyContent="center">
                <CircularProgress />
            </Grid>
        );
    }
    return (
        <Grid className="bg-white activity-edit">
            <form
                onSubmit={ev => {
                    ev.preventDefault();
                }}
            >
                <Grid container spacing={2}>
                    <AdminHeader title={`Payment method  edit`} />
                    {!!error && (
                        <Grid
                            item
                            container
                            spacing={2}
                            xs={12}
                            style={{
                                color: theme.palette.error.main
                            }}
                            alignItems="center"
                        >
                            <Grid item>
                                <ErrorIcon color="error" />
                            </Grid>
                            <Grid item>
                                <span>{error.response.body.message}</span>
                            </Grid>
                        </Grid>
                    )}
                    <FormSection title="Generic information">
                        <Grid item xs={6}>
                            <TextField
                                label="Slug"
                                fullWidth
                                required
                                type="text"
                                value={paymentMethod ? paymentMethod.slug : ''}
                                onChange={ev =>
                                    setPaymentMethod({ ...paymentMethod, slug: ev.target.value })
                                }
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <InputLabel htmlFor={`select`}>Method type</InputLabel>
                            <Select
                                fullWidth
                                value={paymentMethod?.methodtype}
                                onChange={event =>
                                    setPaymentMethod({
                                        ...paymentMethod,
                                        methodtype: event.target.value
                                    })
                                }
                            >
                                {paymentMethodTypeDropdown.map(fieldType => (
                                    <MenuItem key={fieldType.value} value={fieldType.value}>
                                        {fieldType.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                fullWidth
                                required
                                type="text"
                                value={paymentMethod ? paymentMethod.name : ''}
                                onChange={ev =>
                                    setPaymentMethod({ ...paymentMethod, name: ev.target.value })
                                }
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!paymentMethod?.note_required}
                                        onChange={event =>
                                            setPaymentMethod({
                                                ...paymentMethod,
                                                note_required: !!event.target.checked
                                            })
                                        }
                                    />
                                }
                                label={'Note required'}
                            />
                        </Grid>
                    </FormSection>
                    <FormSection title="GL code">
                        <Grid item xs={12}>
                            <GlcodeEditor
                                value={paymentMethod?.gl_code}
                                setValue={glCode =>
                                    setPaymentMethod({ ...paymentMethod, gl_code: glCode })
                                }
                            />
                        </Grid>
                    </FormSection>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                modifyPaymentMethod();
                            }}
                        >
                            Save
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => history.push(`/admin/client/${clientId}/paymentmethods`)}
                        >
                            Back
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Grid>
    );
}

export default withTheme(PaymentMethodEditForm);
